/* eslint-disable camelcase,react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useWalletAPIClient } from '@ledgerhq/wallet-api-client-react';
import { useMutation } from 'react-query';
import { currencies } from '../../../../utilities/constants';
import { TRANSACTION_CALCULATE_FEES, TRANSACTION_SEND } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import { errorCodeIsRiskAssessmentWarning } from '../../../../utilities/helpers';
import { SEND_MODES, SendCryptoScreensFlow } from '../../constants';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { formatNumber } from '../../../../utilities/formatHelpers';
import Alert from '../../../../components/Alert';

const ConfirmNewAmountTransaction = ({ openScreen, onCloseModal, options, onSent }) => {
  const { t: translate } = useTranslation();

  const {
    coin,
    recipient,
    source,
    fees,
    amount,
    isLedgerAddress,
    ledgerAccountId,
    isWhitelistAddress,
    walletBalance,
  } = options;

  const { client } = useWalletAPIClient();

  if (isLedgerAddress && ledgerAccountId && client && !isWhitelistAddress) {
    client.account.receive(ledgerAccountId);
  }

  const receptionAddress = recipient;

  const currency = currencies[coin];

  const { isLoading: isSendingTransaction, mutate: sendTransaction } = useMutation(
    TRANSACTION_SEND,
    {
      onSettled(_, error) {
        if (!error) {
          showAlert({
            message: translate('send_transaction_success_message')
              .replace('__AMOUNT__', amount)
              .replace('__COIN__', coin?.toUpperCase()),
            type: 'success',
          });
          onSent();
        } else if (errorCodeIsRiskAssessmentWarning(error.errorCode)) {
          showAlert({
            message: error.message,
            type: 'warning',
          });
        } else {
          showAlert({ message: error.message, type: 'danger' });
        }
        onCloseModal();
      },
    }
  );

  const { isLoading: isLoadingFees, mutate: calculateTransactionFees } = useMutation(
    TRANSACTION_CALCULATE_FEES,
    {
      onSettled(data, error) {
        if (!error) {
          if (parseFloat(data?.totalAmount) > parseFloat(walletBalance)) {
            showAlert({ message: translate('wallet_balance_less_than_new_fees'), type: 'danger' });
            openScreen(SendCryptoScreensFlow.SEND_CRYPTO);
          } else if (parseFloat(fees.fee) < parseFloat(data?.fee)) {
            openScreen(SendCryptoScreensFlow.CONFIRM_NEW_AMOUNT_TRANSACTION, {
              ...options,
              fees: data,
            });
          } else {
            sendTransaction({
              amount: data?.sendAmount,
              coin,
              currencyId: options?.coin_id,
              source,
              recipient,
              fee: data.fee,
              ...(options.activeVerification ? { otp: options.otp, totp: options.totp } : {}),
            });
          }
        }
      },
    }
  );

  const onSubmit = (e) => {
    e.preventDefault();
    calculateTransactionFees({
      source: options.source,
      destination: options.recipient,
      amount: options.amount,
      mode: options.isTextBit ? SEND_MODES.PHONE_NUMBER : SEND_MODES.ADDRESS,
      coin: options.coin,
      currencyId: options?.coin_id,
    });
  };

  const renderFooter = () => (
    <button
      className="btn btn-primary py-3"
      type="button"
      loading={isSendingTransaction || isLoadingFees ? 'true' : 'false'}
      onClick={onSubmit}>
      {translate('confirm_button_label')}
    </button>
  );

  return (
    <ModalBodyLayout
      withBack
      renderFooter={renderFooter}
      title={translate('confirm_new_amount_label')}
      onPrevClick={() => openScreen(SendCryptoScreensFlow.SEND_CRYPTO)}>
      <p className="text-shadow text-f6 mt-3">{translate('reception_label')}</p>
      <p className="text-f5">{receptionAddress}</p>

      <div className="mt-4">
        <div className="d-flex align-content-between justify-content-between p-3 bg-light rounded-top">
          <b className="text-f5 text-shadow">{translate('amount_label')}</b>
          <p className="text-dark text-f5 upper-case">
            <b>
              {formatNumber(fees.sendAmount, currency.decimal)} {coin}
            </b>
          </p>
        </div>

        <div className="d-flex align-content-between justify-content-between p-3 bg-light rounded-bottom border-top border-disabled">
          <b className="text-f5 text-shadow">{translate('fee_label')}</b>
          <p className="text-dark text-f5 upper-case">
            <b>
              {formatNumber(fees.fee, currency.decimal)} {coin}
            </b>
          </p>
        </div>

        <div className="d-flex align-content-between justify-content-between p-3 mt-3 bg-light rounded-2">
          <b className="text-f5 text-shadow">{translate('total_cost_label')}</b>
          <p className="text-dark text-f5 upper-case">
            <b>
              {formatNumber(fees.totalAmount, currency.decimal)} {coin}
            </b>
          </p>
        </div>
        <div className="mt-4">
          <Alert type="info" icon="info">
            <p className="text-bold text-dark text-f6 letter-spacing-3">
              {translate('fees_updated_info_title')}
            </p>
            <p className="text-dark text-f6 letter-spacing-3 mt-4">
              {translate('fees_updated_info_part_1')}
            </p>
            <p className="text-dark text-f6 letter-spacing-3 mt-4">
              {translate('fees_updated_info_part_2')}
            </p>
          </Alert>
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default ConfirmNewAmountTransaction;

ConfirmNewAmountTransaction.propTypes = {
  openScreen: PropTypes.func.isRequired,
  options: PropTypes.object,
  onCloseModal: PropTypes.func.isRequired,
  onSent: PropTypes.func,
};
ConfirmNewAmountTransaction.defaultProps = {
  options: null,
  onSent: () => {},
};
